import { TenantSearchArgs, TenantSearchFormValues } from '@/features/tenant'
import { useCallback, useEffect, useMemo } from 'react'
import { parseTemplate } from 'url-template'
import { debounce } from '../../../core'
import { tenantsApi as tenantApi } from '@/services/api/tenant/tenantsEnhanced'
import { useTypedDispatch } from '../../../store'
import { useTenantSearchFormContext } from './useTenantSearchFormContext'

const searchQs = parseTemplate('{name}')

export const useSearchTenantsArgs = (
  values?: TenantSearchFormValues,
  page?: number,
  size?: number,
): TenantSearchArgs => {
  return useMemo<TenantSearchArgs>(() => {
    const queryObject: TenantSearchArgs = {
      query: decodeURIComponent(
        searchQs.expand({
          name: (values?.name && `${values?.name}`) || '',
        }),
      ).trim(),
    }
    if (page !== undefined) {
      queryObject.page = page
    }
    if (size !== undefined) {
      queryObject.size = size
    }
    return queryObject
  }, [values, page, size])
}

export const useSearchTenantsState = (values?: TenantSearchFormValues, page?: number, size?: number) => {
  const searchArgs = useSearchTenantsArgs(values, page, size)
  const { data, isFetching, isUninitialized, refetch } = tenantApi.endpoints.listTenants.useQuery(searchArgs)
  return { data, isFetching, isUninitialized, refetch }
}

export const useSearchTenants = (page?: number, size?: number) => {
  const dispatch = useTypedDispatch()
  const { values } = useTenantSearchFormContext()
  const searchArgs = useSearchTenantsArgs(values, page, size)

  const tenantSearchFn = useCallback(
    (args: typeof searchArgs) => {
      dispatch(tenantApi.endpoints.listTenants.initiate(args))
    },
    [dispatch],
  )
  const debouncedTenantSearchFn = useMemo(
    () =>
      debounce((args: typeof searchArgs) => {
        tenantSearchFn(args)
      }, 100),

    [tenantSearchFn],
  )

  useEffect(() => {
    tenantSearchFn(searchArgs)
  }, [])

  useEffect(() => {
    debouncedTenantSearchFn(searchArgs)
  }, [searchArgs, debouncedTenantSearchFn])

  return useSearchTenantsState(values, page, size)
}
